<template>
  <div v-if="visible">
    <div class="panel-backdrop" @click="$emit('close')"></div>
    <div class="panel-drawer">
      <div class="panel-header">
        <span class="panel-title">New Visit</span>
        <button class="panel-close" @click="$emit('close')">✕</button>
      </div>

      <form class="panel-form" @submit.prevent="handleSubmit">
        <div class="form-field">
          <label>Char ID</label>
          <input type="text" :value="nextCharId" readonly class="input-readonly" />
        </div>

        <div class="form-field">
          <label>Date</label>
          <input type="date" v-model="form.date" required />
        </div>

        <div class="form-field">
          <label>Visit</label>
          <input type="number" v-model.number="form.visitNumber" min="1" required placeholder="Visit number" />
        </div>

        <div class="form-field">
          <label>Type</label>
          <input type="text" value="Biennial" readonly class="input-readonly" />
        </div>

        <div class="form-field">
          <label>Interviewer</label>
          <UserSearchSelect
            v-model="form.interviewerPortalUserId"
            :users="portalUsers"
            :disabled="loadingUsers"
            :placeholder="loadingUsers ? 'Loading...' : 'Search interviewer...'"
          />
        </div>

        <div v-if="error" class="form-error">{{ error }}</div>

        <div class="form-actions">
          <button type="button" class="btn-cancel" @click="$emit('close')">Cancel</button>
          <button type="submit" class="btn-submit" :disabled="submitting">
            {{ submitting ? 'Creating...' : 'Create Visit' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import ACTApiService from '@/services/ACTApiService';
import UserSearchSelect from '@/components/UserSearchSelect.vue';

const api = new ACTApiService();

export default {
  name: 'NewVisitPanel',
  components: { UserSearchSelect },
  props: {
    visible: { type: Boolean, default: false },
    rows: { type: Array, default: () => [] },
    lGroupId: { type: String, default: null }
  },
  emits: ['close', 'visit-created'],
  data() {
    return {
      form: {
        date: '',
        visitNumber: '',
        interviewerPortalUserId: ''
      },
      portalUsers: [],
      loadingUsers: false,
      submitting: false,
      error: null
    };
  },
  computed: {
    nextCharId() {
      const nums = this.rows
        .map(r => r.participantDisplayId)
        .filter(id => /^ACTB\d+$/.test(id))
        .map(id => parseInt(id.slice(4)));
      return `ACTB${nums.length ? Math.max(...nums) + 1 : 300}`;
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetForm();
        this.fetchPortalUsers();
      }
    }
  },
  methods: {
    resetForm() {
      this.form = { date: '', visitNumber: '', interviewerPortalUserId: '' };
      this.error = null;
    },
    async fetchPortalUsers() {
      this.loadingUsers = true;
      try {
        this.portalUsers = await api.getPortalUsers();
      } catch (e) {
        console.warn('Could not load portal users:', e);
        this.portalUsers = [];
      } finally {
        this.loadingUsers = false;
      }
    },
    async handleSubmit() {
      this.error = null;
      this.submitting = true;
      const charId = this.nextCharId;
      const lGroupId = this.lGroupId || api.getStoredActGroupId();
      const visitTypeId = localStorage.getItem('biennialVisitTypeId') || '';

      try {
        const visitData = { date: this.form.date, visitNumber: this.form.visitNumber };
        if (this.form.interviewerPortalUserId) visitData.interviewerPortalUserId = this.form.interviewerPortalUserId;
        const visitResult = await api.createVisit(lGroupId, charId, visitTypeId, charId, visitData);

        const visitId = visitResult?.visitId ?? visitResult?.data?.visitId ?? charId;

        if (this.form.interviewerPortalUserId) {
          await api.createWorkflowTask(
            'Interview',
            charId,
            visitId,
            visitTypeId,
            this.form.interviewerPortalUserId
          );
        }

        this.$emit('visit-created', {
          charId,
          visitId,
          visitTypeId,
          date: this.form.date,
          visitNumber: this.form.visitNumber,
          interviewerPortalUserId: this.form.interviewerPortalUserId
        });
      } catch (e) {
        this.error = e.message || 'Failed to create visit. Please try again.';
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.panel-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 100;
}

.panel-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  height: 100vh;
  background: white;
  z-index: 101;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.panel-title {
  font-size: 17px;
  font-weight: 600;
  color: #237091;
}

.panel-close {
  background: none;
  border: none;
  font-size: 16px;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
}
.panel-close:hover { background: #f3f4f6; color: #374151; }

.panel-form {
  flex: 1;
  overflow-y: auto;
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-field label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.form-field input,
.form-field select {
  padding: 9px 12px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #111;
  outline: none;
  transition: border-color 0.15s;
}

.form-field input:focus,
.form-field select:focus {
  border-color: #3986A8;
}

.input-readonly {
  background: #f9fafb !important;
  color: #6b7280 !important;
  cursor: default;
}

.form-error {
  font-size: 13px;
  color: #dc2626;
  background: #fee2e2;
  border-radius: 6px;
  padding: 10px 12px;
}

.form-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 4px;
}

.btn-cancel {
  padding: 8px 18px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #555;
  cursor: pointer;
}
.btn-cancel:hover { background: #f9fafb; }

.btn-submit {
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  background: #3986A8;
  color: white;
  cursor: pointer;
}
.btn-submit:hover:not(:disabled) { background: #237091; }
.btn-submit:disabled { opacity: 0.5; cursor: default; }
</style>
